/**
 * Sentry vía Loader Script oficial (https://docs.sentry.io/platforms/javascript/install/loader/).
 * Ventajas: el SDK se actualiza solo (Sentry controla la versión), config base
 * viene del dashboard, y podemos sobre-escribir con Sentry.onLoad(init) para
 * agregar `environment`, `release`, sampling, filtros.
 */

let loaderKey = null;
let queued = [];

function loaderUrlFor(key) {
  return `https://js.sentry-cdn.com/${encodeURIComponent(key)}.min.js`;
}

function withSentry(fn) {
  if (typeof window === 'undefined') return;
  if (window.Sentry && typeof window.Sentry.addBreadcrumb === 'function') {
    try {
      fn(window.Sentry);
    } catch (_e) {
      /* ignore */
    }
    return;
  }
  queued.push(fn);
}

function flushQueue() {
  if (typeof window === 'undefined' || !window.Sentry) return;
  const pending = queued;
  queued = [];
  for (const f of pending) {
    try {
      f(window.Sentry);
    } catch (_e) {
      /* ignore */
    }
  }
}

export function initMlvSentry({ loaderKey: key, environment = 'production', release } = {}) {
  if (loaderKey || !key || typeof window === 'undefined') return;
  loaderKey = key;
  try {
    const s = document.createElement('script');
    s.src = loaderUrlFor(key);
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.onload = () => {
      try {
        // `Sentry.onLoad` está disponible sólo con el Loader Script; se ejecuta
        // cuando el SDK real termina de cargar (lazy). Acá sobre-escribimos config
        // y drenamos breadcrumbs / setUser que se hayan acumulado antes.
        if (window.Sentry && typeof window.Sentry.onLoad === 'function') {
          window.Sentry.onLoad(() => {
            try {
              window.Sentry.init({
                environment,
                release,
                tracesSampleRate: 0.1,
                ignoreErrors: [
                  'ResizeObserver loop limit exceeded',
                  'Non-Error promise rejection captured',
                  'Network Error',
                ],
              });
            } catch (e) {
              console.warn('[MLV] Sentry init falló:', e?.message || e);
            }
            flushQueue();
          });
        } else {
          flushQueue();
        }
      } catch (e) {
        console.warn('[MLV] Sentry onLoad falló:', e?.message || e);
      }
    };
    s.onerror = () => {
      /* adblocker / sin red: ignorar silenciosamente */
    };
    document.head.appendChild(s);
  } catch (e) {
    console.warn('[MLV] Sentry load falló:', e?.message || e);
  }
}

export function mlvSentryBreadcrumb(name, data = {}) {
  withSentry((S) => {
    S.addBreadcrumb({ category: 'mlv', message: name, level: 'info', data });
  });
}

export function mlvSentrySetUser(uid) {
  withSentry((S) => {
    S.setUser(uid ? { id: String(uid) } : null);
  });
}

export function mlvSentryCaptureException(err, ctx = {}) {
  withSentry((S) => {
    S.captureException(err, { extra: ctx });
  });
}
